import { orderSourceLabel } from '@/utils/orderSource';
/**
 * UploadView - Order document upload and extraction interface
 * Handles PDF/image uploads, AI extraction, and session order management
 */
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import useDocumentIntake from '@/hooks/useDocumentIntake';
import useRecentOrderDeletion from '@/hooks/useRecentOrderDeletion';
import {
  getOrder,
  getOrders,
} from '@/api/orders';
import { formatPhoneNumberDisplay } from '@/utils/phoneUtils';
import EditOrderDialog from '@/components/EditOrderDialog';
import ManualOrderForm from '@/components/ManualOrderForm';
import SessionOrdersList from '@/components/SessionOrdersList';
import OrderReviewView from '@/components/OrderReviewView';
import { isDesktopOffline } from '@/api/desktopSync';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { manualOrderPrincipalKey } from '@/utils/manualOrderAttempts';

const UploadView = () => {
  const { sessionBinding } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const principalKey = manualOrderPrincipalKey(sessionBinding);
  const receiptOwners = useRef(new Map());
  const currentOwner = useRef(null);
  currentOwner.current = { principalKey, revision, bindingRevision: sessionBinding?.revision };
  const [sessionOrders, setSessionOrders] = useState([]); // Only orders from current session
  const [editingOrder, setEditingOrder] = useState(null);
  const [reviewingOrder, setReviewingOrder] = useState(null);
  const [reviewOpeningId, setReviewOpeningId] = useState(0);
  const reviewSelection = useRef(null);
  reviewSelection.current = { orderId: reviewingOrder?.id, openingId: reviewOpeningId };
  const offline = isDesktopOffline();
  const deletion = useRecentOrderDeletion();

  useEffect(() => {
    setEditingOrder(order => receiptOwners.current.has(order?.id) ? null : order);
    setReviewingOrder(order => receiptOwners.current.has(order?.id) ? null : order);
  }, [revision, principalKey]);

  // Both intake channels publish only under their captured account/session.
  // Never rebrand an old receipt after a session change.
  const ownsReceipt = owner => {
    const current = currentOwner.current;
    return Boolean(owner?.principalKey && owner.principalKey === current.principalKey
      && owner.revision === current.revision && owner.revision === current.bindingRevision
      && owner.revision === getActiveSessionRevision());
  };
  const visibleOrder = order => !receiptOwners.current.has(order?.id)
    || ownsReceipt(receiptOwners.current.get(order.id));
  const ownsContinuation = (orderId, owner) => !owner
    || (receiptOwners.current.get(orderId) === owner && ownsReceipt(owner));
  const visibleSessionOrders = sessionOrders.filter(order => visibleOrder(order) && !deletion.isRemoved(order));
  const currentOrders = useRef(null);
  currentOrders.current = sessionOrders.filter(visibleOrder);
  const acceptOrder = (order, owner) => {
    if (!order?.id || !owner?.isCurrent?.() || !ownsReceipt(owner)) return;
    receiptOwners.current.set(order.id, { principalKey: owner.principalKey, revision: owner.revision });
    setSessionOrders(previous => [order, ...previous.filter(item => item.id !== order.id)]);
  };

  const documents = useDocumentIntake(acceptOrder);
  const { record, files, pending: loading } = documents;
  const batchResults = record?.entries || [];
  const settledCount = batchResults.filter(entry => !['draft', 'pending'].includes(entry.status)).length;
  const progressPercent = batchResults.length ? settledCount / batchResults.length * 100 : 0;
  const processingStatus = loading ? 'Processing original document submissions…' : '';
  const hasAccepted = batchResults.some(entry => ['accepted', 'warning'].includes(entry.status));
  const canReset = batchResults.length > 0 && batchResults.every(entry =>
    ['draft', 'accepted', 'warning', 'refused'].includes(entry.status));
  const canSubmit = batchResults.some(entry => !['accepted', 'warning', 'refused'].includes(entry.status));
  const [resetArmed, setResetArmed] = useState(false);
  useEffect(() => {
    setResetArmed(false);
  }, [record, revision]);
  useEffect(() => {
    if (!resetArmed) return undefined;
    const timer = setTimeout(() => setResetArmed(false), 5000);
    return () => clearTimeout(timer);
  }, [resetArmed]);
  const handleFileChange = event => {
    if (!offline) documents.selectFiles(Array.from(event.target.files || []));
    // The controller owns retained files; do not retain accepted documents in
    // the native input's FileList after they have been released by the owner.
    event.target.value = '';
  };
  const handleReset = () => {
    if (!resetArmed) { setResetArmed(true); return; }
    documents.reset();
    setResetArmed(false);
  };

  const handleDelete = async (orderId) => {
    const receipt = receiptOwners.current.get(orderId);
    const order = visibleSessionOrders.find(item => item.id === orderId);
    if (!order) return;
    await deletion.remove(order, () => currentOrders.current.includes(order)
      && ownsContinuation(orderId, receipt));
  };

  return (
    <div className="min-h-screen bg-stone-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-stone-900 mb-2">Order Creation</h1>
          <p className="text-stone-600">Upload order documents or manually enter delivery information</p>
        </div>

        {/* Upload Section */}
        <Card
          data-testid="upload-card"
          className={`bg-stone-100 border-stone-300 ${offline ? 'opacity-50' : ''}`}
          aria-disabled={offline}
        >
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-[2fr_7fr] gap-6">
              {/* Title + subtext */}
              <div className="space-y-1.5">
                <CardTitle>Upload Order Documents</CardTitle>
                <CardDescription>PDF, PNG, JPG</CardDescription>
              </div>

              {/* File selection + extraction */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pdf-upload">Order Files</Label>
                  <Input
                    id="pdf-upload"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
                    multiple
                    onChange={handleFileChange}
                    disabled={offline || documents.locked}
                    data-testid="pdf-file-input"
                  />
                </div>

                {files.length > 0 && (
                  <div className="space-y-2" data-testid="selected-files-info">
                    <p className="text-sm font-medium text-stone-700">
                      {files.length} file{files.length > 1 ? 's' : ''} selected:
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {files.map((f, index) => (
                        <div key={`${index}-${f.name}`} className="flex items-center gap-2 text-sm text-stone-600">
                          <FileText className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{f.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {processingStatus && (
                  <div className="space-y-2" data-testid="processing-status">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-primary font-medium">{processingStatus}</span>
                      {loading && files.length > 0 && (
                        <span className="text-stone-400 text-xs tabular-nums">
                          {progressPercent.toFixed(0)}%
                        </span>
                      )}
                    </div>
                    {loading && files.length > 1 && (
                      <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <Button
                  onClick={() => documents.submit()}
                  disabled={offline || !canSubmit || loading}
                  className="w-full"
                  data-testid="upload-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Extracting...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      {record?.started ? 'Continue original submissions' : 'Extract Information'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2" data-testid="document-recovery-controls">
          <p className="text-sm text-stone-600">
            Up to 20 files, 5MB each. Unfinished submissions are kept only in this open tab;
            reloading or closing it loses this recovery information.
          </p>
          {record?.message && <p role="status">{record.message}</p>}
          <div className="flex justify-end gap-2">
            {hasAccepted && <Button variant="outline" onClick={() => documents.publish()}
              disabled={offline || loading} data-testid="document-show-saved">
              Show saved orders
            </Button>}
            {canReset && <Button variant="outline" onClick={handleReset}
              disabled={loading} data-testid="document-clear-selection">
              {resetArmed ? 'Confirm clear local selection' : 'Clear local selection'}
            </Button>}
          </div>
        </div>

        <ManualOrderForm onOrderCreated={acceptOrder} />

        {/* Batch Results Display */}
        {batchResults && batchResults.length > 0 && (
          <Card className="border-blue-200 bg-primary/5" data-testid="batch-results-card">
            <CardHeader>
              <CardTitle className="text-blue-900">Batch Extraction Results</CardTitle>
              <CardDescription>
                {['accepted', 'warning', 'refused', 'processing', 'unknown', 'draft', 'pending'].map(status =>
                  `${batchResults.filter(item => item.status === status).length} ${status}`).join(', ')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {batchResults.map((result) => (
                  <div
                    key={result.key}
                    className={`border rounded-xl p-3 ${
                      ['accepted', 'warning'].includes(result.status) ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}
                    data-testid={`batch-result-${result.filename}`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <FileText className={`w-4 h-4 mt-0.5 ${['accepted', 'warning'].includes(result.status) ? 'text-green-600' : 'text-red-600'}`} />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{result.filename}</p>
                        {['accepted', 'warning'].includes(result.status) ? (
                          <p className="text-xs text-green-700">{result.message || 'Order saved'}</p>
                        ) : (
                          <p className="text-xs text-red-700">{result.message || result.status}</p>
                        )}
                      </div>
                    </div>
                    
                    {['accepted', 'warning'].includes(result.status) && result.receipt?.order && (
                      <div className="grid grid-cols-2 gap-2 text-xs mt-2 pt-2 border-t border-green-200">
                        <div>
                          <span className="font-medium">Name: </span>
                          <span>{result.receipt?.order.recipient_name || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="font-medium">Phone: </span>
                          <span>{formatPhoneNumberDisplay(result.receipt?.order.recipient_phone) || 'N/A'}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="font-medium">Address: </span>
                          <span>
                            {result.receipt?.order.recipient_street_address && result.receipt?.order.recipient_city
                              ? `${result.receipt?.order.recipient_street_address}, ${result.receipt?.order.recipient_city}, ${result.receipt?.order.recipient_state} ${result.receipt?.order.recipient_zip_code}`
                              : 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Order #: </span>
                          <span>{result.receipt?.order.order_number || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="font-medium">Network: </span>
                          <span>{orderSourceLabel(result.receipt?.order, 'N/A')}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <SessionOrdersList
          key={deletion.key}
          orders={visibleSessionOrders}
          deletion={deletion}
          confirmationIdentity={order => receiptOwners.current.get(order.id) || order}
          onEdit={order => { if (visibleOrder(order)) setEditingOrder(order); }}
          onReview={order => {
            if (visibleOrder(order)) {
              setReviewOpeningId(value => value + 1);
              setReviewingOrder(order);
            }
          }}
          onDelete={handleDelete}
        />

        <EditOrderDialog
          order={visibleOrder(editingOrder) ? editingOrder : null}
          onClose={() => setEditingOrder(null)}
          onSaved={async (_orderId, context) => {
            // Refresh the edited order in session state
            if (editingOrder && visibleOrder(editingOrder) && context?.owns()) {
              const owner = receiptOwners.current.get(editingOrder.id);
              try {
                const orders = await getOrders(undefined, context.config);
                const fresh = orders.find(o => o.id === editingOrder.id);
                if (fresh && context.owns() && ownsContinuation(editingOrder.id, owner)) {
                  setSessionOrders(prev => prev.map(o => o.id === fresh.id ? fresh : o));
                }
              } catch (e) {
                if (context.owns()) throw e;
              }
            }
          }}
        />

        <OrderReviewView
          order={visibleOrder(reviewingOrder) ? reviewingOrder : null}
          openingId={reviewOpeningId}
          onClose={() => setReviewingOrder(null)}
          onSaved={async (orderId, context) => {
            // TCA-26101502: refresh belongs to the captured Review opening and
            // attempt, not whichever order/session is selected when it returns.
            const owner = receiptOwners.current.get(orderId);
            const owns = () => context?.owns() && !context.config?.signal?.aborted
              && reviewSelection.current?.openingId === reviewOpeningId
              && reviewSelection.current?.orderId === orderId
              && ownsContinuation(orderId, owner);
            if (!orderId || reviewingOrder?.id !== orderId || !owns()) return undefined;
            const fresh = await getOrder(orderId, context.config);
            if (!owns()) return undefined;
            if (!fresh || typeof fresh !== 'object' || Array.isArray(fresh) || fresh.id !== orderId) {
              throw new Error('The reviewed order could not be refreshed.');
            }
            setSessionOrders(prev => prev.map(order => order.id === orderId ? fresh : order));
            return fresh;
          }}
        />
      </div>
    </div>
  );
};

export default UploadView;
